<template>
  <div>
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      v-show="!getItemLoadingStatus"
    >
      <v-card max-width="650px">
        <v-card-title>
          <span class="headline" v-if="isEditView">{{ editedItem.name }}</span>
          <span class="headline" data-cy="itemAddNewText" v-else>{{
            $t("components.items.itemAddEditForm.addNew")
          }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <!-- NAME AND MODEL-->
              <v-col cols="12" sm="12" md="12" lg="12">
                <item-suggester
                  :name.sync="editedItem.name"
                  :rules="productNameRule"
                  @update:item="applyTemplate"
                  :disable-suggestions="true"
                  data-cy="itemProductNameInput"
                ></item-suggester>
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <vendor-selector
                  :vendor_uuid.sync="editedItem.vendor_uuid"
                  :initial_data.sync="editedItem.vendor"
                  :rules.sync="manufacturerNameRule"
                  :key="forceVendorRender"
                  data-cy="itemManufacturerNameInput"
                />
              </v-col>

              <!-- DOCUMENTS SELECTOR -->
              <v-col
                cols="12"
                sm="12"
                md="12"
                lg="12"
                style="padding-bottom: 0px"
              >
                <v-row class="pl-3">
                  <h3 class="subtitle-1 mt-1">
                    {{ $t("components.items.itemAddEditForm.documents") }}
                  </h3>
                  <v-spacer></v-spacer>

                  <licence-tooltip
                    :feature="createNewDocumentsHandler"
                    :disabled="createNewDocumentsHandler.disabled"
                    :enabled="createNewDocumentsHandler.enabled"
                    float-direction="right"
                  >
                    <template v-slot:action="{ attrs, disabled, enabled }">
                      <v-btn
                        v-if="
                          getItemPath === 'itemsEdit' ||
                          getItemPath === 'itemsAdd'
                        "
                        class="float-right"
                        color="primary"
                        text
                        @click="openDocumentDialog()"
                        data-cy="itemOpenDocumentDialogBtn"
                        v-bind="attrs"
                        :disabled="disabled"
                      >
                        <v-icon small class="mr-1" v-if="enabled">
                          mdi-star-outline </v-icon
                        >{{
                          $t("components.items.itemAddEditForm.createDocument")
                        }}</v-btn
                      >
                    </template>
                  </licence-tooltip>
                </v-row>
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12" style="padding-top: 4px">
                <document-picker
                  :unavailableDocuments="[]"
                  :documents="editedItem.documents"
                  :can-user-edit="editedItem.is_editable"
                  v-on:add:document="editedItem.documents.push($event)"
                  v-on:delete:document="editedItem.documents.splice($event, 1)"
                  feature="add-document-on-part"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <item-categories-autocomplete
                  :categories.sync="editedItem.categories"
                  data-cy="itemCategoriesInput"
                  :hint="
                    $t('components.items.itemAddEditForm.categoryIsRecommended')
                  "
                />
              </v-col>

              <!-- WARRANTY AND DESCRIPTION -->

              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  v-model="editedItem.warranty_months"
                  :label="$t('components.items.itemAddEditForm.warrantyPeriod')"
                  color="primary"
                  :hint="$t('components.items.itemAddEditForm.months')"
                  type="number"
                  min="1"
                  data-cy="itemWarrantyPeriodInput"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  v-model="editedItem.extended_warranty_months"
                  :label="
                    $t(
                      'components.items.itemAddEditForm.extendedWarrantyPeriod'
                    )
                  "
                  color="primary"
                  :hint="$t('components.items.itemAddEditForm.months')"
                  type="number"
                  min="1"
                  data-cy="itemExtendedWarrantyPeriodInput"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  v-model="editedItem.sku"
                  :label="$t('components.items.itemAddEditForm.sku')"
                  color="primary"
                  data-cy="itemSkuInput"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  v-model="editedItem.manufacturer_product_code"
                  :label="$t('components.items.itemAddEditForm.productCode')"
                  color="primary"
                  data-cy="itemProductCodeInput"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-textarea
                  v-model="editedItem.description"
                  :label="$t('components.items.itemAddEditForm.description')"
                  color="primary"
                  auto-grow
                  data-cy="itemDescriptionInput"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <group-selector
                  :crm-group-uuid.sync="editedItem.crm_group_uuid"
                  :initial-data="editedItem.crm_group"
                  :select-default="!isEditView"
                  :rules.sync="groupRule"
                  :uuid="editViewUuid"
                />
              </v-col>

              <v-col cols="12" sm="12" md="12" lg="12">
                <v-row>
                  <v-col cols="6">
                    <h3 class="subtitle-1">
                      {{
                        $t("components.items.itemAddEditForm.serviceProtocol")
                      }}
                    </h3>
                  </v-col>
                  <v-col cols="6">
                    <licence-tooltip
                      :feature="customProtocolsHandler"
                      :disabled="customProtocolsHandler.disabled"
                      :enabled="customProtocolsHandler.enabled"
                      :float-direction="'right'"
                    >
                      <template v-slot:action="{ attrs, disabled, enabled }">
                        <v-btn
                          text
                          color="primary"
                          class="float-right"
                          @click="openImportDialog()"
                          data-cy="itemImportServiceProtocolBtn"
                          v-bind="attrs"
                          :disabled="disabled"
                        >
                          <v-icon small v-if="enabled" class="mr-1">
                            mdi-star-outline
                          </v-icon>
                          {{ $t("components.items.itemAddEditForm.import") }}
                        </v-btn>
                      </template>
                    </licence-tooltip>
                    <licence-tooltip
                      :feature="customProtocolsHandler"
                      :disabled="customProtocolsHandler.disabled"
                      :enabled="customProtocolsHandler.enabled"
                      :float-direction="'right'"
                    >
                      <template v-slot:action="{ attrs, disabled, enabled }">
                        <v-btn
                          text
                          color="primary"
                          class="float-right"
                          @click="openService()"
                          data-cy="itemAddNewServiceProtocolBtn"
                          v-bind="attrs"
                          :disabled="disabled"
                        >
                          <v-icon small v-if="enabled" class="mr-1">
                            mdi-star-outline
                          </v-icon>
                          {{ $t("components.items.itemAddEditForm.addNew") }}
                        </v-btn>
                      </template>
                    </licence-tooltip>
                  </v-col>
                </v-row>
                <v-row
                  v-for="(service, index) in editedItem.crm_services"
                  v-bind:key="service.uuid"
                >
                  <v-col cols="1" sm="1" md="1" lg="1">
                    <v-icon class="align-center ml-2 mt-6" medium
                      >mdi-wrench-outline</v-icon
                    >
                  </v-col>
                  <v-col cols="11" sm="11" md="11" lg="11">
                    <service-interval
                      :service="service"
                      :index="index"
                      @delete-service-protocol="
                        editedItem.crm_services.splice(index, 1)
                      "
                    ></service-interval>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            data-cy="itemCancelBtn"
            text
            @click="close()"
            >{{ $t("components.items.itemAddEditForm.cancel") }}</v-btn
          >
          <licence-tooltip
            :feature="customProtocolsHandler"
            :disabled="customProtocolsHandler.disabled"
            :enabled="customProtocolsHandler.enabled"
            :float-direction="'right'"
          >
            <template v-slot:action="{ attrs, disabled, enabled }">
              <v-btn
                color="primary"
                text
                @click="save(true)"
                v-bind="attrs"
                :disabled="disabled"
              >
                <v-icon small class="mr-1" v-if="enabled">
                  mdi-star-outline
                </v-icon>
                {{ $t("components.items.itemAddEditForm.saveAsNew") }}
              </v-btn>
            </template>
          </licence-tooltip>
          <v-btn
            color="primary"
            data-cy="itemSaveBtn"
            text
            @click="save(false)"
            >{{ $t("components.items.itemAddEditForm.save") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-form>
    <inline-document-dialog></inline-document-dialog>
    <service-protocol-import-dialog
      :dialog="serviceImportDialog"
      @import="serviceImportHandler"
    ></service-protocol-import-dialog>
  </div>
</template>

<script>
import api from "@/utils/api.js";
import ServiceInterval from "@/components/miscellaneous/ServiceInterval";
import VendorSelector from "@/components/selectors/VendorSelector";
import GroupSelector from "@/components/selectors/GroupSelector";
import DocumentPicker from "@/components/pickers/DocumentPicker";
import ItemCategoriesAutocomplete from "@/components/items/ItemCategoriesAutocomplete";
import ItemSuggester from "@/components/items/ItemSuggester";
import snackbar from "@/utils/snackbar.js";
import InlineDocumentDialog from "@/components/dialogs/InlineDocumentDialog";
import ServiceProtocolImportDialog from "@/components/dialogs/ServiceProtocolImportDialog";
import { mapGetters } from "vuex";
import LicenceTooltip from "@/components/general/LicenceTooltip";

export default {
  components: {
    ItemSuggester,
    ItemCategoriesAutocomplete,
    DocumentPicker,
    GroupSelector,
    VendorSelector,
    ServiceInterval,
    InlineDocumentDialog,
    ServiceProtocolImportDialog,
    LicenceTooltip,
  },
  data: () => ({
    serviceImportDialog: false,
    valid: true,
    productNameRule: [],
    manufacturerNameRule: [],
    groupRule: [],
    editedIndex: -1,
    loading: true,
    service: null,
    isEditView: false,
    editViewUuid: null,
    forceVendorRender: 0,
    editedItem: {
      uuid: null,
      name: "",
      categories: [],
      crm_group_uuid: null,
      crm_services: [],
      documents: [],
      is_boat_specific: false,
      start_date: new Date(),
      vendor: null,
      vendor_uuid: null,
      warranty_months: null,
    },
  }),

  computed: {
    ...mapGetters([
      "getItemPath",
      "getItemLoadingStatus",
      "customProtocolsHandler",
      "createNewDocumentsHandler",
    ]),
  },

  created() {
    this.setInitialData();
    if (this.$route.name === "itemsEdit") {
      this.$store.commit("ITEMPATH", "itemsEdit");
      this.isEditView = true;
      this.editViewUuid = this.$route.params.id;
      this.initItem(this.$route.params.id);
    } else {
      this.$store.commit("ITEMPATH", this.$route.name);
      this.editViewUuid = "new";
    }
    setTimeout(() => {
      this.$watch("editedItem", this.formEdited, {
        deep: true,
      });
    }, 3000);
  },
  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (
        mutation.type === "DOCUMENTADDED" &&
        (state.document.from === "itemsEdit" ||
          state.document.from === "itemsAdd")
      ) {
        this.editedItem.documents.push(mutation.payload.document);
      }
    });
  },

  updated() {
    // FIX
    // this.$nextTick(function() {
    //   this.$watch("editedItem", this.formEdited, {
    //     deep: true,
    //   });
    //   });
  },

  methods: {
    setInitialData() {
      this.productNameRule = [
        (v) =>
          !!v ||
          this.$t("components.items.itemAddEditForm.productNameIsRequired"),
      ];
      this.manufacturerNameRule = [
        (v) =>
          !!v ||
          this.$t(
            "components.items.itemAddEditForm.manufacturerNameIsRequired"
          ),
      ];
      this.groupRule = [
        (v) =>
          !!v || this.$t("components.items.itemAddEditForm.groupIsRequired"),
      ];
    },
    formEdited(newV, oldV) {
      this.$emit("itemEdited", true);
    },
    openImportDialog() {
      this.serviceImportDialog = true;
    },

    serviceImportHandler(value) {
      if (value) {
        value.forEach((element) => {
          element.intervals.forEach((item) => {
            delete item.uuid;
          });
          this.editedItem.crm_services.push(element);
        });
      }
      this.serviceImportDialog = false;
    },

    initItem(uuid) {
      api.getItem(uuid, { include_public: true }).then((resp) => {
        this.editedItem = resp.data;
        if (this.isEditView) {
          this.editedItem.vendor_uuid = this.editedItem.vendor
            ? this.editedItem.vendor.uuid
            : null;
          this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
        } else {
          if (this.editedItem.vendor) {
            this.editedItem.vendor = {
              name: this.editedItem.vendor.name,
              uuid: Math.round(Math.random() * 1000),
            };
            this.editedItem.vendor_uuid = this.editedItem.vendor.uuid;
          }
        }
        this.forceVendorRender++;
      });
    },
    openDocumentDialog() {
      this.$store.commit("DOCUMENTPATH", this.$route.name);
      this.$store.commit("DOCUMENTDIALOGSTATUS", true);
    },

    applyTemplate(value) {
      this.initItem(value.uuid);
    },
    openService() {
      this.editedItem.crm_services.unshift(this.serviceTemplate());
    },
    serviceTemplate: () => {
      return {
        short_task: "",
        long_task: "",
        subtasks: [],
        intervals: [
          {
            periodic: true,
            source: "ENGINE_HOURS",
            value: 0,
          },
        ],
      };
    },
    close(uuid) {
      if (
        (this.getItemPath === "itemsEdit" || this.getItemPath === "itemsAdd") &&
        typeof uuid !== "undefined"
      ) {
        this.$router.push({
          name: "itemsDetails",
          params: { id: uuid },
        });
      } else if (
        this.getItemPath === "itemsEdit" ||
        this.getItemPath === "itemsAdd"
      ) {
        this.$router.push({
          name: "items",
        });
      } else {
        this.$store.commit("ITEMDIALOGSTATUS", false);
      }
    },
    handleError(err) {
      if (err.status === 403) {
        snackbar.error(
          this.$t("utils.snackbar.error.dontHavePermissionToEditItems")
        );
        this.$emit("itemEdited", false);
        this.close();
      } else if (err.status === 400 && Object.keys(err.data)[0] === "sku") {
        snackbar.error(
          this.$t("utils.snackbar.error.anErrorHasOccurred") +
            ": " +
            JSON.stringify(err.data[Object.keys(err.data)[0]][0])
        );
      } else if (err.status === 400) {
        snackbar.error(
          this.$t("utils.snackbar.error.anErrorHasOccurred") +
            ": " +
            JSON.stringify(err.data[Object.keys(err.data)[0]][0])
        );
        this.$emit("itemEdited", false);
        this.close();
      } else {
        snackbar.error(this.$t("utils.snackbar.error.anErrorHasOccurred"));
        this.$emit("itemEdited", false);
        this.close();
      }
      this.$store.commit("ITEMLOADING", false);
    },
    createNewVendor() {
      return api.createVendor({
        name: this.editedItem.vendor.name,
        crm_group_uuid: this.editedItem.crm_group_uuid,
      });
    },
    createNewItem() {
      //false parameter is here for disabling error handling
      api
        .createItem(this.editedItem, false)
        .then((resp) => {
          //this.loading = false;
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          this.$store.commit("ITEMLOADING", false);
          this.$store.commit("ITEMADDED", { item: resp.data });
          this.$emit("itemEdited", false);
          this.close(resp.data.uuid);
        })
        .catch((err) => {
          this.handleError(err.response);
        });
    },
    updateItem() {
      //false parameter is here for disabling error handling
      api
        .updateItem(this.editedItem.uuid, this.editedItem, false)
        .then((resp) => {
          this.$store.commit("ITEMLOADING", false);
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          this.$emit("itemEdited", false);
          this.close(resp.data.uuid);
        })
        .catch((err) => {
          this.handleError(err.response);
        });
    },
    save(asNew) {
      if (this.$refs.form.validate()) {
        if (!this.isEditView) {
          this.editedItem.crm_services.forEach((element) => {
            delete element.uuid;
            element.intervals.forEach((el) => {
              delete el.uuid;
            });
          });
        }
        this.editedItem.document_uuids = this.editedItem.documents.map(
          (doc) => doc.uuid
        );

        this.editedItem.categories = this.editedItem.categories.map(
          (category) => {
            if (typeof category.category !== "undefined") {
              return category.category;
            } else {
              return category;
            }
          }
        );

        if (this.editedItem.warranty_months === "") {
          this.editedItem.warranty_months = null;
        }
        if (this.editedItem.extended_warranty_months === "") {
          this.editedItem.extended_warranty_months = null;
        }

        let vendorPromise = null;
        if (
          typeof this.editedItem.vendor_uuid !== "undefined" &&
          this.editedItem.vendor_uuid !== null &&
          this.editedItem.vendor !== null &&
          this.editedItem.vendor_uuid.toString().length < 30 &&
          this.editedItem.vendor.name !== null
        ) {
          vendorPromise = this.createNewVendor()
            .then((resp) => {
              this.editedItem.vendor_uuid = resp.data.uuid;
              this.isEditView && !asNew
                ? this.updateItem()
                : this.createNewItem();
            })
            .catch((err) => {
              this.handleError(err.response);
            });
        } else {
          if (
            this.editedItem.vendor_uuid === null ||
            this.editedItem.vendor === null ||
            typeof this.editedItem.vendor === "undefined" ||
            this.editedItem.vendor.name === null
          ) {
            this.editedItem.vendor_uuid = null;
          }
          this.isEditView && !asNew ? this.updateItem() : this.createNewItem();
        }
      } else {
        snackbar.error();
      }
    },
  },
};
</script>

<style></style>
